import React, { useEffect } from 'react'
import { useHistory } from '@fs/zion-router'
import ZionForm from '@fs/zion-form'
import { ContentCheck } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { DialogOverlay, DialogOverlayActions, useStatusOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'
import type { Playlist } from '../../../api/rootstech/playlist/playlist.types'
import { FLAGS, playlistsConfig } from '../../../config'
import { useFlagsContext, usePlaylistContainerContext } from '../../../providers'
import { usePlaylistCreation } from '../../../hooks/playlist/usePlaylistCreation'
import notifySentry from '../../../lib/helpers/sentryNotifier'
import PillButton from '../../../lib/buttons/PillButton' // Needs to be direct or circular dep.
import PlaylistTitleTextField from './PlaylistTitleTextField'
import PlaylistDescriptionTextField from './PlaylistDescriptionTextField'
import usePlaylistForm from '../hooks/usePlaylistForm'

type CreatePlaylistOverlayProps = UseOverlayReturnOptions & {
  sessionId?: string
  onPlaylistCreated?: (playlist: Playlist) => void
}

export default function CreatePlaylistOverlay({
  sessionId,
  onPlaylistCreated,
  ...overlay
}: CreatePlaylistOverlayProps): JSX.Element {
  const [t] = useTranslation()
  const history = useHistory()
  const showStatusOverlay = useStatusOverlay()
  const { [FLAGS.NEW_PLAYLIST_STATE]: newPlaylistStateFlag } = useFlagsContext()

  // Old provider-based state (deprecated)
  const oldContext = usePlaylistContainerContext()
  const { createPlaylist: oldCreatePlaylist } = oldContext || {}

  // New React Query-based state
  const { createPlaylist: createPlaylistMutation } = usePlaylistCreation()

  const { methods } = usePlaylistForm({ name: '', description: '' })

  useEffect(() => methods.setFocus('name'), [methods])

  const onSubmit = async ({ name, description }: { name: string; description: string }): Promise<void> => {
    try {
      if (newPlaylistStateFlag.isOn) {
        // New React Query implementation handles success/error automatically
        const response = await createPlaylistMutation({ name, description, initialItem: sessionId })
        const results = response.data
        methods.reset(undefined, { keepValues: false })
        onPlaylistCreated?.(results)
      } else {
        // Old provider-based implementation
        const results = await oldCreatePlaylist?.({ name, description, initialItem: sessionId || undefined })
        if (!results) return
        methods.reset(undefined, { keepValues: false })

        showStatusOverlay({
          message: `${t('playlists.alerts.created-new', 'Created playlist { playlistName }.', { playlistName: name })}`,
          type: 'success',
          Icon: ContentCheck,
          actionLabel: t('playlists.saved.nav-to-playlist', 'Go to Playlist'),
          actionCallback: () => history.push(`/playlists/${results.id}`),
        })

        onPlaylistCreated?.(results)
      }
    } catch (err: unknown) {
      if (!newPlaylistStateFlag.isOn) {
        // Old implementation handles errors manually
        const error = err as { response?: { status?: number; [key: string]: unknown }; [key: string]: unknown }
        let message = t(
          'playlists.errors.generic.creation-error',
          "We're sorry, but there was an issue creating your new playlist. Please try again shortly or refresh your browser."
        )
        if (error?.response?.status === 422) {
          message = t(
            'playlists.alerts.playlist-limit-reached',
            'You have reached the maximum limit of {max} playlists. Please delete any old or non-essential playlists to make room for new ones.',
            {
              max: playlistsConfig.playlistLimit,
            }
          )
        } else {
          notifySentry({ ...error?.response, err: error })
        }

        showStatusOverlay({
          message,
          type: 'error',
          dismissable: true,
        })
      }
      // New implementation handles errors in the hook
    }
  }
  return (
    <DialogOverlay
      autoFocus
      {...overlay}
      title={t('playlists.create.title', 'Create New Playlist')}
      closeOnClickAway={false}
      footer={
        <DialogOverlayActions>
          <PillButton
            emphasis="medium"
            type="submit"
            form="create-playlist-form"
            Icon={ContentCheck}
            linkName="Save New Playlist"
            data-testid="save-new-playlist"
          >
            {t('common-ui:save.action', 'Save')}
          </PillButton>
        </DialogOverlayActions>
      }
    >
      <ZionForm id="create-playlist-form" methods={methods} onSubmit={onSubmit}>
        <PlaylistTitleTextField />
        <PlaylistDescriptionTextField />
      </ZionForm>
    </DialogOverlay>
  )
}
